import { useRef } from 'react';
import { useFormik } from 'formik';
import { ResourceResponse } from "types";
import { useTypedSelector } from 'hooks';
import { useSimpleRequest } from "hooks/useRequest";
import { SetupIdentifier } from 'consts/setups';
import { Filters } from 'consts/transactions';
import { updateConsumer } from "features/consumers/api";
import type { Consumer } from "features/consumers/types";
import { useConsumersActions } from 'features/consumers/actions';
import { selectSetupsSlice } from 'features/setups/selectors';
import { useModal } from "ui/atoms/Modal";
import type { ConsumerIDVerification } from "ui/forms/Consumer";
import { TabIndex, TabProps } from "ui/organizms/Consumers/ConsumerDialogs/MultiTabDialog/types";
import useMarkAsKYC from "../useMarkAsKYC";

type UseIDVerificationConsumerDetails =
    & ReturnType<typeof useModal>
    & TabProps<TabIndex.MarkAsKYCed>
    & Pick<ReturnType<typeof useMarkAsKYC>, 'onMarkAsKYCed'>;

export default function useIDVerificationConsumerDetails({
    onMarkAsKYCed,
    onClose,
    getTabPayload
}: UseIDVerificationConsumerDetails) {
    const domains = useTypedSelector(selectSetupsSlice(SetupIdentifier.Mors));
    const formikRef = useRef<ReturnType<typeof useFormik>>(null);
    const request = useSimpleRequest();

    const { updateConsumerById } = useConsumersActions();

    const onSuccess = (resourceResponse: ResourceResponse<Consumer>) => {
        updateConsumerById(resourceResponse.data);
        onMarkAsKYCed();
    };

    const onSubmit = async (formValues: Partial<ConsumerIDVerification.FormValues>) => {
        await request<Consumer>(() => updateConsumer({
            DOB: formValues.DOB,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            verifiedDob: formValues.DOB,
            verifiedFirstName: formValues.firstName,
            verifiedLastName: formValues.lastName,
            coreId: getTabPayload<Consumer>().coreId,
            [Filters.MOR]: domains[Filters.MOR]
        }), {
            onSuccess
        });

        onClose();
    };

    const handleClose = () => {
        if (!formikRef.current?.isSubmitting) {
            onClose();
        }
    };

    return {
        onSubmit,
        handleClose,
        formikRef
    };
};
